import { Link, useParams, useNavigate } from "react-router-dom";
import { decodeUrlParam } from "../../utils/urlUtils";
import { Panel, PanelHeader, PanelBody } from "./../../components/panel/panel.jsx";
import StaffTabBar from "./staffTabBar.js";
import { useState, useEffect, useCallback } from "react";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import PasswordRequirementsInfo from "../../components/CustomComponents/PasswordRequirementsInfo/PasswordRequirementsInfo.jsx";
import { useSelector } from "react-redux";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import confirmUnsavedChangesSwal from "../../utils/confirmUnsavedChangesSwal";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";

const UpdatePassword = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { UserName: encodedUserName } = useParams();
  const UserName = decodeUrlParam(encodedUserName);
  const navigate = useNavigate();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staffDisplayName, setStaffDisplayName] = useState("");
  // Initial loaded data snapshot (empty for password form) – used to detect changes and avoid unnecessary save/API call
  const [initialLoadedDataSnapshot, setInitialLoadedDataSnapshot] = useState(null);

  const fetchStaffName = useCallback(async () => {
    if (!CompID || !UserName) return;
    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
        params: { CompID, UserName },
      });
      const staff = response.data.data || response.data;
      const first = staff?.firstName ?? staff?.FirstName ?? "";
      const last = staff?.lastName ?? staff?.LastName ?? "";
      setStaffDisplayName([first, last].filter(Boolean).join(" ").trim() || UserName);
    } catch {
      setStaffDisplayName(UserName);
    }
  }, [CompID, UserName]);

  useEffect(() => {
    fetchStaffName();
  }, [fetchStaffName]);

  const getSnapshot = useCallback(() => JSON.stringify({ newPassword, confirmNewPassword }), [newPassword, confirmNewPassword]);
  const isDirty = initialLoadedDataSnapshot !== null && getSnapshot() !== initialLoadedDataSnapshot;

  useEffect(() => {
    setInitialLoadedDataSnapshot(getSnapshot());
  }, []);

  const handleSubmit = async (e, options = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    const { exitAfterSave = false } = options;

    // No changes from initial loaded data – skip API; if Save and Exit just navigate
    if (initialLoadedDataSnapshot !== null && getSnapshot() === initialLoadedDataSnapshot) {
      if (exitAfterSave) navigate("/staff/manageStaff");
      return true;
    }

    if (newPassword !== confirmNewPassword) {
      showErrorToast("Passwords do not match.");
      return false;
    }

    if (!newPassword.trim()) {
      showErrorToast("Please enter a new password.");
      return false;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.post(APIs.UPDATE_PASSWORD, {
        UserName,
        Password: newPassword,
        ConfirmPassword: newPassword
      },{
        params: {
          CompID
        },
      });
      if (response.data.status) {
        showSuccessToast("Password updated successfully.");
        setNewPassword("");
        setConfirmNewPassword("");
        setInitialLoadedDataSnapshot(JSON.stringify({ newPassword: "", confirmNewPassword: "" }));
        if (exitAfterSave) {
          navigate("/staff/manageStaff");
        }
        return true;
      } else {
        showErrorToast(response.data.message);
        return false;
      }
    } catch (err) {
      showErrorToast("Something went wrong.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleTabChangeBeforeNavigate = useCallback(
    async (clickedTabId, clickedTabLink) => {
      if (saving || loading) return false;
      // No changes from initial loaded data – allow switch without warning
      if (!isDirty) return true;

      const result = await confirmUnsavedChangesSwal();

      if (!result) return false;
      if (result === "discard") return true;
      if (result === "save") {
        const success = await handleSubmit(null, { exitAfterSave: false });
        if (success && clickedTabLink) navigate(clickedTabLink);
        return false;
      }
      return false;
    },
    [isDirty, saving, loading, navigate, handleSubmit]
  );

  return (
    <div>
    

      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/staff/manageStaff">Manage Staff</Link>
            </li>
            <li className="breadcrumb-item active">Update Password</li>
          </ol>
          <h1 className="page-header mb-0">
            Update Password – {staffDisplayName || UserName} ({UserName})
          </h1>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          <button
            type="button"
            onClick={() => handleSubmit(null, { exitAfterSave: false })}
            disabled={saving}
            className="btn px-4"
            style={{
              background: isDarkMode ? "#495057" : "#6c757d",
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            <i className="fa fa-save fa-lg me-2 ms-n2 text-white"></i>
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit(null, { exitAfterSave: true })}
            disabled={saving}
            className="btn px-4"
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            <i className="fa fa-save fa-lg me-2 ms-n2 text-white"></i>
            {saving ? "Saving..." : "Save and Exit"}
          </button>
        </div>
      </div>
      <GridSectionLoader loading={loading || saving} minHeight="280px">
<div className="card border-0">
        <StaffTabBar
          activeTab="updatePassword"
          useTab="false"
          UserName={UserName}
          onBeforeTabChange={handleTabChangeBeforeNavigate}
        />
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            <div className="row mb-3">
              <div className="col-6">
                <FormInput
                  label="New Password"
                  placeHolder="Enter New Password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onchange={(e) => setNewPassword(e.target.value)}
                  icon={showNewPassword ? "fa-eye-slash" : "fa-eye"}
                  iconOnClick={() => setShowNewPassword((prev) => !prev)}
                  labelAddon={<PasswordRequirementsInfo />}
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Confirm New Password"
                  placeHolder="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmNewPassword}
                  onchange={(e) => setConfirmNewPassword(e.target.value)}
                  icon={showConfirmPassword ? "fa-eye-slash" : "fa-eye"}
                  iconOnClick={() => setShowConfirmPassword((prev) => !prev)}
                />
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>
    </div>
  );
};

export default UpdatePassword;